/**
 * "Another customer of this tenant is already at this address."
 *
 * Advisory, never blocking: a shared address is often legitimate (a household,
 * a landlord and tenant, the previous owner after a transfer), so the person
 * saving sees who shares it and decides. ``AddressMatchNotice`` warns inside the
 * edit dialog while the address is typed; ``SharedAddressBadge`` keeps the fact
 * visible on the Customer card afterwards. Both read the same tenant-scoped
 * endpoint, which only internal staff can call.
 */

import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import {
  useCustomerAddressMatches,
  useDraftAddressMatches,
  type AddressMatches,
} from '@/admin/hooks/use-address-matches'
import { formatRegistrationReference } from '@/admin/lib/formatting'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value'

function useMatchTitle(total: number): string {
  const { t } = useTranslation()
  return total === 1
    ? t('customers.addressMatchOne', {
        defaultValue: 'Another customer is already at this address',
      })
    : t('customers.addressMatchOther', {
        count: total,
        defaultValue: '{{count}} other customers are already at this address',
      })
}

function AddressMatchList({ data }: { data: AddressMatches }): ReactNode {
  const { t } = useTranslation()
  const hidden = data.total - data.matches.length
  return (
    <ul className="mt-2 flex flex-col gap-2" data-testid="address-match-list">
      {data.matches.map((match) => (
        <li key={match.consumer_uuid} className="text-[12.5px] leading-[1.45]">
          <div className="flex flex-wrap items-center gap-1.5">
            <a
              href={`/customers/${match.consumer_uuid}`}
              target="_blank"
              rel="noreferrer"
              className="font-normal text-[var(--ink)] underline-offset-2 hover:underline"
            >
              {match.name || match.email}
            </a>
            {match.relation ? (
              <Badge variant="outline" className="text-[10px] uppercase tracking-[0.08em]">
                {match.relation === 'previous_owner'
                  ? t('customers.addressMatchPreviousOwner', {
                      defaultValue: 'Previous owner',
                    })
                  : t('customers.addressMatchNewOwner', { defaultValue: 'New owner' })}
              </Badge>
            ) : null}
          </div>
          {match.registrations.length > 0 ? (
            <div className="mt-0.5 flex flex-wrap gap-x-2 font-mono text-[11px] text-[var(--ink-3)]">
              {match.registrations.map((registration) => (
                <a
                  key={registration.uuid}
                  href={`/registrations/${registration.uuid}`}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-[var(--ink)]"
                >
                  {formatRegistrationReference(registration)}
                </a>
              ))}
              {match.registration_total > match.registrations.length ? (
                // The customer record lists every one of them.
                <a
                  href={`/customers/${match.consumer_uuid}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="address-match-more-registrations"
                  className="font-sans text-[var(--ink-3)] underline-offset-2 hover:text-[var(--ink)] hover:underline"
                >
                  {t('customers.addressMatchMoreRegistrations', {
                    count: match.registration_total - match.registrations.length,
                    defaultValue: '+{{count}} more',
                  })}
                </a>
              ) : null}
            </div>
          ) : null}
        </li>
      ))}
      {hidden > 0 ? (
        <li className="text-[11.5px] text-[var(--ink-3)]">
          {t('customers.addressMatchMore', {
            count: hidden,
            defaultValue: 'and {{count}} more',
          })}
        </li>
      ) : null}
    </ul>
  )
}

function asAddress(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/** Warning under the address in the edit dialog, from the value being entered. */
export function AddressMatchNotice({
  consumerUuid,
  value,
}: {
  consumerUuid: string
  value: unknown
}): ReactNode {
  const { t } = useTranslation()
  const address = useDebouncedValue(asAddress(value), 400)
  const { data } = useDraftAddressMatches(consumerUuid, address)
  const title = useMatchTitle(data?.total ?? 0)
  if (!data || data.total === 0) return null
  return (
    <div
      role="status"
      data-testid="address-match-notice"
      className="rounded-md border-[0.5px] border-[var(--gold-border)] bg-[var(--gold-bg)] px-3 py-2.5"
    >
      <p className="text-[12.5px] font-medium text-[var(--gold-ink)]">{title}</p>
      <AddressMatchList data={data} />
      <p className="mt-2 text-[11.5px] text-[var(--gold-ink)]">
        {t('customers.addressMatchSaveNote', {
          defaultValue: 'You can still save. The match is recorded in the activity log.',
        })}
      </p>
    </div>
  )
}

/** "Shared address" marker for a Customer card, with who shares it. */
export function SharedAddressBadge({
  consumerUuid,
  holdingUuid,
}: {
  consumerUuid: string
  holdingUuid?: string
}): ReactNode {
  const { t } = useTranslation()
  const { data } = useCustomerAddressMatches(consumerUuid, holdingUuid)
  const title = useMatchTitle(data?.total ?? 0)
  if (!data || data.total === 0) return null
  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            size="xs"
            data-testid="shared-address-badge"
            className="h-6 rounded-[4px] border-[var(--gold-border)] bg-[var(--gold-bg)] px-2 text-[10.5px] font-normal uppercase tracking-[0.1em] text-[var(--gold-ink)] hover:bg-[var(--gold-bg)]"
          >
            {t('customers.addressMatchBadge', { defaultValue: 'Shared address' })}
          </Button>
        }
      />
      <PopoverContent side="bottom" align="end" className="max-w-[320px]">
        <p className="text-[12.5px] font-medium text-[var(--ink)]">{title}</p>
        <AddressMatchList data={data} />
      </PopoverContent>
    </Popover>
  )
}
